/**
 * EndGameSummaryModal — 12/02/2026
 *
 * Modale de bilan de fin de partie affichée avant le reset via
 * "Quitter la partie". Le joueur voit une synthèse de sa progression
 * puis confirme (ou annule).
 */
import React from 'react';
import {
  Trophy, Users, Truck, Landmark, Newspaper, Award, TrendingUp, TrendingDown,
  Building2, Star, X,
} from 'lucide-react';
import { formatMoney } from '../utils/format';
import { buildEndGameSummary } from '../utils/endGameSummary';

function KpiCard({ icon: Icon, label, value, sub, tone = 'default', testId }) {
  const toneClasses = {
    default: 'border-ink-600 bg-ink-800',
    money: 'border-emerald-500/30 bg-emerald-500/5',
    danger: 'border-rose-500/30 bg-rose-500/5',
    warn: 'border-amber-500/30 bg-amber-500/5',
    accent: 'border-accent/30 bg-accent/5',
  }[tone] || 'border-ink-600 bg-ink-800';
  return (
    <div className={`p-3 rounded-lg border ${toneClasses}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider mb-1">
        <Icon className="w-3 h-3" />
        <span>{label}</span>
      </div>
      <p className="text-lg font-bold text-foreground">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function EndGameSummaryModal({ state, onConfirm, onCancel }) {
  const summary = React.useMemo(() => buildEndGameSummary(state), [state]);
  if (!summary) return null;

  const {
    company, phase, totalDays, yearsPlayed,
    finalTreasury, treasuryDelta,
    totalRevenue, totalNet,
    finalFleet, finalHeadcount, totalDeparted,
    acquiredCompetitors, pressMilestones, lastEcoVadis,
    achievementCount, bestYear, worstYear, topClients,
  } = summary;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="end-game-summary-modal"
    >
      <div className="bg-ink-900 border border-ink-500 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="px-6 py-5 border-b border-ink-600 bg-gradient-to-r from-accent/10 to-transparent">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                <Trophy className="w-3.5 h-3.5 text-amber-400" />
                <span className="uppercase tracking-widest">Bilan de partie</span>
              </div>
              <h2 className="text-2xl font-bold text-foreground" data-testid="end-game-company-name">
                {company.name}
              </h2>
              <p className="text-xs text-slate-400 mt-1">
                Phase {phase} · {yearsPlayed} an{yearsPlayed > 1 ? 's' : ''} · {totalDays} jours de jeu
              </p>
            </div>
            <button aria-label="Continuer la partie"
              onClick={onCancel}
              className="p-1.5 rounded-md hover:bg-ink-700 text-slate-400 hover:text-foreground transition"
              data-testid="end-game-cancel-btn"
              title="Continuer la partie"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* KPIs financiers */}
        <div className="px-6 py-4 border-b border-ink-600">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 font-mono mb-3 flex items-center gap-1.5">
            <Landmark className="w-3 h-3" /> Finances
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KpiCard
              icon={Landmark} label="Trésorerie finale"
              value={formatMoney(finalTreasury)}
              sub={treasuryDelta !== 0 ? `${treasuryDelta > 0 ? '+' : ''}${formatMoney(treasuryDelta)} vs début` : null}
              tone={finalTreasury > 0 ? 'money' : 'danger'}
              testId="end-game-treasury"
            />
            <KpiCard
              icon={TrendingUp} label="CA cumulé"
              value={formatMoney(totalRevenue)}
              tone="money"
              testId="end-game-revenue"
            />
            <KpiCard
              icon={totalNet >= 0 ? TrendingUp : TrendingDown}
              label="Résultat net cumulé"
              value={formatMoney(totalNet)}
              tone={totalNet >= 0 ? 'money' : 'danger'}
              testId="end-game-net"
            />
            <KpiCard
              icon={Award} label="Succès débloqués"
              value={`${achievementCount}`}
              sub={lastEcoVadis ? `EcoVadis ${lastEcoVadis.medal || lastEcoVadis.tier || '—'}` : null}
              tone="accent"
              testId="end-game-achievements"
            />
          </div>
        </div>

        {/* KPIs opérationnels */}
        <div className="px-6 py-4 border-b border-ink-600">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 font-mono mb-3 flex items-center gap-1.5">
            <Truck className="w-3 h-3" /> Opérations
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KpiCard icon={Truck} label="Flotte finale" value={`${finalFleet} camions`} testId="end-game-fleet" />
            <KpiCard icon={Users} label="Effectif final" value={`${finalHeadcount} chauffeurs`} testId="end-game-headcount" />
            <KpiCard
              icon={Users} label="Départs cumulés"
              value={`${totalDeparted}`}
              sub="démissions + licenciements"
              tone={totalDeparted > 5 ? 'warn' : 'default'}
              testId="end-game-departures"
            />
            <KpiCard
              icon={Building2} label="Concurrents rachetés"
              value={`${acquiredCompetitors.length}`}
              sub={acquiredCompetitors.length > 0 ? acquiredCompetitors.map(c => c.name).join(', ') : null}
              tone={acquiredCompetitors.length > 0 ? 'accent' : 'default'}
              testId="end-game-acquisitions"
            />
          </div>
        </div>

        {/* Meilleure / Pire année */}
        {(bestYear || worstYear) && (
          <div className="px-6 py-4 border-b border-ink-600">
            <h3 className="text-xs uppercase tracking-wider text-slate-400 font-mono mb-3">
              Année phare · Année difficile
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {bestYear && (
                <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5" data-testid="end-game-best-year">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Meilleure année · An {bestYear.year}</p>
                  <p className="text-sm text-foreground">Résultat net : <span className="font-bold text-money">{formatMoney(bestYear.netResult)}</span></p>
                  <p className="text-[11px] text-slate-500">CA {formatMoney(bestYear.totalRevenue)}</p>
                </div>
              )}
              {worstYear && worstYear !== bestYear && (
                <div className="p-3 rounded-lg border border-rose-500/30 bg-rose-500/5" data-testid="end-game-worst-year">
                  <p className="text-[10px] uppercase tracking-wider text-rose-300 mb-1">Année difficile · An {worstYear.year}</p>
                  <p className="text-sm text-foreground">Résultat net : <span className="font-bold text-danger">{formatMoney(worstYear.netResult)}</span></p>
                  <p className="text-[11px] text-slate-500">CA {formatMoney(worstYear.totalRevenue)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Top clients */}
        {topClients.length > 0 && (
          <div className="px-6 py-4 border-b border-ink-600">
            <h3 className="text-xs uppercase tracking-wider text-slate-400 font-mono mb-3 flex items-center gap-1.5">
              <Star className="w-3 h-3" /> Meilleurs clients
            </h3>
            <ol className="space-y-1.5" data-testid="end-game-top-clients">
              {topClients.map((c, i) => (
                <li key={c.id} className="flex items-center justify-between text-xs">
                  <span className="text-foreground">
                    <span className="text-slate-500 font-mono mr-2">#{i + 1}</span>
                    {c.name}
                    <span className="text-slate-500"> · {c.contracts} contrat{c.contracts > 1 ? 's' : ''}</span>
                  </span>
                  <span className="font-mono text-money">{formatMoney(c.totalRevenue)}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Moments-clés narratif presse */}
        {pressMilestones.length > 0 && (
          <div className="px-6 py-4 border-b border-ink-600">
            <h3 className="text-xs uppercase tracking-wider text-slate-400 font-mono mb-3 flex items-center gap-1.5">
              <Newspaper className="w-3 h-3" /> Moments-clés · Actu Transport
            </h3>
            <ul className="space-y-1.5" data-testid="end-game-press-milestones">
              {pressMilestones.map(m => (
                <li key={m.threshold} className="flex items-center gap-2 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  <span className="text-foreground italic">« {m.label} »</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-4 flex items-center justify-end gap-3 bg-ink-900/60">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-md text-xs text-slate-300 hover:bg-ink-700 transition"
            data-testid="end-game-cancel-cta"
          >
            Continuer la partie
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-md text-xs bg-danger text-white hover:bg-danger/80 transition font-medium"
            data-testid="end-game-confirm-quit-btn"
          >
            Confirmer et quitter
          </button>
        </div>
      </div>
    </div>
  );
}
